import {Button} from '@chekinapp/ui';
import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {
  Offer,
  PriceItem,
  DEAL_STATUSES,
  OFFER_CONFIRMATION_TYPES,
  QUERY_CACHE_KEYS,
} from '@chekin/core';
import {Check, ChevronDown, Clock, ShoppingCart} from 'lucide-react';
import {PriceBadge} from '@guestapp/old-ui-kit';
import {usePaymentSettings, useGuestGroup, useCreateBulkDealMutation} from 'hooks';
import {useReservation} from 'context/reservation';
import {formatPrice, getPrice} from 'components/upselling/price';
import {Counter} from 'components/common/Counter';
import {CounterSize} from 'components/common/Counter/styled';

export type DiscountInfo = {
  percentage: number;
  originalPrice: string;
};
import {
  DealCardContainer,
  CardImageContainer,
  CardImage,
  CardGradientOverlay,
  SwipeIndicator,
  CardContent,
  CardTitle,
  CardHighlight,
  CardActions,
  PriceBadgeContainer,
  QuantityContainer,
  QuantityLabel,
  QuantityTitle,
  QuantityPrice,
  TimeBasedSelectionContainer,
  TimeBasedTitle,
  TimeDropdown,
  TimeDropdownButton,
  TimeDropdownButtonContent,
  TimeDropdownValue,
  TimeDropdownPrice,
  TimeDropdownMenu,
  TimeOption,
  TimeOptionLabel,
  TimeOptionPrice,
  TimeOptionCheck,
  TotalPriceRow,
  TotalLabel,
  TotalAmount,
} from './styled';
import {isTimeBasedOffer} from 'utils/upselling';

const SWIPE_THRESHOLD = 100;

type UnitType = PriceItem['unit_type'];

const getPricePerKey = (unitType: UnitType) =>
  `upselling_add_to_card_form.price.${unitType?.toLocaleLowerCase() || 'per_unit'}`;

type DealCardWithCartProps = {
  offer: Offer;
  stackIndex: number;
  isDragging: boolean;
  translateX: number;
  rotation: number;
  isExiting: boolean;
  exitDirection: 'left' | 'right' | null;
  discount?: DiscountInfo;
  onSkip: () => void;
  onAddedToCart: () => void;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragMove: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragEnd: () => void;
};

export const DealCardWithCart = React.memo(function DealCardWithCart({
  offer,
  stackIndex,
  isDragging,
  translateX,
  rotation,
  isExiting,
  exitDirection,
  discount,
  onSkip,
  onAddedToCart,
  onDragStart,
  onDragMove,
  onDragEnd,
}: DealCardWithCartProps) {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const {currencySymbol} = usePaymentSettings();
  const {data: reservation} = useReservation();
  const {guestGroup} = useGuestGroup();

  const [quantity, setQuantity] = useState(1);
  const [selectedTimeItemId, setSelectedTimeItemId] = useState<string | undefined>(
    () => offer.items?.[0]?.id,
  );
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const {mutate: createDeal, isPending} = useCreateBulkDealMutation({
    onSuccess: () => {
      setIsAdding(false);
      void queryClient.invalidateQueries({queryKey: [QUERY_CACHE_KEYS.paymentsCart]});
      onAddedToCart();
    },
    onError: () => {
      setIsAdding(false);
    },
  });

  const skipOpacity = Math.min(1, Math.max(0, -translateX / 100));
  const addOpacity = Math.min(1, Math.max(0, translateX / 100));

  const isTimeBasedDeal = isTimeBasedOffer(offer);
  const selectedTimeItem =
    offer.items?.find(item => item.id === selectedTimeItemId) ?? offer.items?.[0];
  const firstItem = offer.items?.[0];
  const unitPrice = isTimeBasedDeal
    ? Number(selectedTimeItem?.price ?? 0)
    : getPrice(offer);
  const dealQuantity = isTimeBasedDeal ? 1 : quantity;
  const totalPrice = unitPrice * dealQuantity;

  let displayPrice = formatPrice(currencySymbol)({
    priceType: offer.price_type,
    items: offer.items,
  });

  if (!displayPrice && offer.items?.[0]?.price) {
    displayPrice = `${currencySymbol}${offer.items[0].price}`;
  }

  if (isTimeBasedDeal && selectedTimeItem) {
    displayPrice = `${currencySymbol}${Number(selectedTimeItem.price).toFixed(2)}`;
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (stackIndex !== 0) return;
    e.preventDefault();
    onDragStart(e);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (stackIndex !== 0) return;
    onDragStart(e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (stackIndex !== 0 || !isDragging) return;
    onDragMove(e);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (stackIndex !== 0) return;
    onDragMove(e);
  };

  const handleDragEnd = () => {
    if (stackIndex !== 0) return;

    if (translateX >= SWIPE_THRESHOLD) {
      triggerAddToCart();
    }
    onDragEnd();
  };

  const handleSkipClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSkip();
    },
    [onSkip],
  );

  const handleQuantityChange = useCallback(
    (value: number) => {
      if (!isTimeBasedDeal) {
        setQuantity(value);
      }
    },
    [isTimeBasedDeal],
  );

  const handleTimeItemChange = useCallback((itemId: string, value: number) => {
    if (value > 0) {
      setSelectedTimeItemId(itemId);
      setIsTimeDropdownOpen(false);
    }
  }, []);

  const stopTimeDropdownEvent = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
      e.stopPropagation();
    },
    [],
  );

  const selectedTimeLabel = selectedTimeItem
    ? (selectedTimeItem.name || '').split(':').splice(0, 2).join(':')
    : '';

  const selectedTimePrice = selectedTimeItem
    ? `${currencySymbol}${Number(selectedTimeItem.price).toFixed(2)}`
    : '';

  const hasMultipleTimeOptions = offer.items.length > 1;

  const toggleTimeDropdown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsTimeDropdownOpen(isOpen => !isOpen);
  }, []);

  const handleTimeOptionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
      e.stopPropagation();
      e.preventDefault();
      handleTimeItemChange(itemId, 1);
    },
    [handleTimeItemChange],
  );

  const triggerAddToCart = useCallback(() => {
    if (!reservation || !offer || isAdding || isPending) {
      return;
    }

    const coreGuest = guestGroup?.members.find(({id}) => id === guestGroup?.leader_id);
    const requestedItemId = isTimeBasedDeal ? selectedTimeItem?.id : firstItem?.id;

    if (!requestedItemId) {
      return;
    }

    setIsAdding(true);

    const guestEmail = coreGuest?.email || reservation.default_invite_email || undefined;

    createDeal({
      deals: [
        {
          core_housing_id: reservation.housing_id,
          core_reservation_id: reservation.id,
          core_guest_name: coreGuest?.full_name,
          core_housing_name: reservation.housing_name,
          core_room_numbers: reservation.rooms?.map(room => room.number) || [],
          guest_email: guestEmail,
          core_guest_id: coreGuest?.id,
          offer_id: offer.id,
          quantity: 1,
          payments_version: 'V2' as const,
          number_of_people: dealQuantity,
          requested_items: [
            {
              item: requestedItemId,
              quantity: dealQuantity,
            },
          ],
          status:
            offer.confirmation_type === OFFER_CONFIRMATION_TYPES.auto
              ? DEAL_STATUSES.approved
              : DEAL_STATUSES.pre_requested,
        },
      ],
    });
  }, [
    reservation,
    offer,
    guestGroup,
    isTimeBasedDeal,
    selectedTimeItem?.id,
    firstItem?.id,
    dealQuantity,
    createDeal,
    isAdding,
    isPending,
  ]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      triggerAddToCart();
    },
    [triggerAddToCart],
  );

  return (
    <DealCardContainer
      $isDragging={isDragging}
      $translateX={translateX}
      $rotation={rotation}
      $stackIndex={stackIndex}
      $isExiting={isExiting}
      $exitDirection={exitDirection}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleDragEnd}
    >
      {stackIndex === 0 && (
        <>
          <SwipeIndicator $direction="left" $opacity={skipOpacity}>
            {t('skip')}
          </SwipeIndicator>
          <SwipeIndicator $direction="right" $opacity={addOpacity}>
            {t('enhance_your_stay.add_to_cart')}
          </SwipeIndicator>
        </>
      )}

      <CardImageContainer>
        <CardImage src={offer.image_url} alt={offer.title} />
        <CardGradientOverlay />
        <PriceBadgeContainer>
          <PriceBadge
            price={displayPrice || t('enhance_your_stay.view_price')}
            originalPrice={discount?.originalPrice}
            discountPercentage={discount?.percentage}
          />
        </PriceBadgeContainer>
      </CardImageContainer>

      <CardContent>
        <CardTitle>{offer.title}</CardTitle>
        <CardHighlight>{offer.highlight}</CardHighlight>

        {isTimeBasedDeal ? (
          <TimeBasedSelectionContainer
            onClick={stopTimeDropdownEvent}
            onMouseDown={stopTimeDropdownEvent}
            onTouchStart={stopTimeDropdownEvent}
          >
            <TimeBasedTitle>
              <Clock size={16} />
              <span>{t('select_time')}</span>
            </TimeBasedTitle>
            <TimeDropdown>
              <TimeDropdownButton
                type="button"
                onClick={toggleTimeDropdown}
                disabled={!hasMultipleTimeOptions}
                aria-expanded={isTimeDropdownOpen}
              >
                <TimeDropdownButtonContent>
                  <TimeDropdownValue>{selectedTimeLabel}</TimeDropdownValue>
                  <TimeDropdownPrice>{selectedTimePrice}</TimeDropdownPrice>
                </TimeDropdownButtonContent>
                {hasMultipleTimeOptions && <ChevronDown size={18} />}
              </TimeDropdownButton>
              {isTimeDropdownOpen && hasMultipleTimeOptions && (
                <TimeDropdownMenu>
                  {offer.items.map(item => {
                    const isSelected = item.id === selectedTimeItem?.id;
                    const itemLabel = (item.name || '').split(':').splice(0, 2).join(':');

                    return (
                      <TimeOption
                        key={item.id}
                        type="button"
                        $selected={isSelected}
                        onClick={e => handleTimeOptionClick(e, item.id)}
                      >
                        <TimeOptionLabel>{itemLabel}</TimeOptionLabel>
                        <TimeOptionPrice>
                          {currencySymbol}
                          {Number(item.price).toFixed(2)}
                        </TimeOptionPrice>
                        {isSelected && (
                          <TimeOptionCheck>
                            <Check size={12} />
                          </TimeOptionCheck>
                        )}
                      </TimeOption>
                    );
                  })}
                </TimeDropdownMenu>
              )}
            </TimeDropdown>
          </TimeBasedSelectionContainer>
        ) : (
          <QuantityContainer onClick={e => e.stopPropagation()}>
            <QuantityLabel>
              <QuantityTitle>{t('enhance_your_stay.quantity')}</QuantityTitle>
              <QuantityPrice>
                {currencySymbol}
                {unitPrice.toFixed(2)} / {t(getPricePerKey(firstItem?.unit_type))}
              </QuantityPrice>
            </QuantityLabel>
            <Counter
              value={dealQuantity}
              onChange={handleQuantityChange}
              minNumber={1}
              maxNumber={99}
              size={CounterSize.S}
            />
          </QuantityContainer>
        )}

        <TotalPriceRow>
          <TotalLabel>{t('enhance_your_stay.total')}</TotalLabel>
          <TotalAmount>
            {currencySymbol}
            {totalPrice.toFixed(2)}
          </TotalAmount>
        </TotalPriceRow>

        <CardActions>
          <Button
            variant="destructive"
            onClick={handleSkipClick}
            size="s"
            disabled={stackIndex !== 0 || isAdding || isPending}
          >
            {t('skip')}
          </Button>
          <Button
            size="s"
            onClick={handleAddToCart}
            disabled={stackIndex !== 0 || isAdding || isPending}
          >
            <ShoppingCart size={16} />
            {isAdding || isPending
              ? t('enhance_your_stay.adding')
              : t('enhance_your_stay.add_to_cart')}
          </Button>
        </CardActions>
      </CardContent>
    </DealCardContainer>
  );
});
